/**
 * CameraRig.ts — Tilted top-down camera that follows the player.
 *
 * The camera sits back and above the player at CAMERA.TILT_DEG below horizontal
 * and looks at them, so you see building faces and cast shadows — that's what makes
 * the scene read as 3D rather than as a flat overhead map.
 *
 * The follow is damped (exponential smoothing) rather than rigid, so the leash
 * yanking the player around doesn't jerk the camera.
 */
import * as THREE from 'three';
import { CAMERA } from '../config/constants';
import { activeLevel } from '../config/level';
import { smoothing } from '../utils/math';
import type { Vec2 } from '../utils/vec2';

export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  /**
   * Smoothed focus point the camera looks at (world x/z). Starts at the middle
   * of whatever map is loaded — `snapTo` moves it onto the player before the
   * first frame, so this only matters for the instant before that.
   */
  private focus: THREE.Vector3;
  /** Camera offset from the focus point, derived from tilt + distance. */
  private offset: THREE.Vector3;

  /** Where the camera is currently looking, on the ground plane. */
  get focusPoint(): { x: number; z: number } {
    return { x: this.focus.x, z: this.focus.z };
  }

  constructor() {
    // The middle of the *walk*, not of the map: a generated town carries on
    // past the playable rectangle, so the middle of the map can be out in the
    // surround entirely.
    const bounds = activeLevel().bounds;
    this.focus = new THREE.Vector3(bounds.x + bounds.w / 2, 0, bounds.y + bounds.h / 2);
    this.camera = new THREE.PerspectiveCamera(CAMERA.FOV, 1, 1, 3000);

    // Tilt is measured from the horizon: 90° would be straight down. The camera
    // pulls back along +z by cos(tilt) and up by sin(tilt).
    //
    // The +z here is load-bearing, not arbitrary. Sitting the camera on the −z
    // side instead (and so looking toward +z) mirrors BOTH ground axes on screen:
    // world +x renders leftward and world +z renders up the page. That inverts
    // every movement key at once, and quietly flips the UK left-hand driving into
    // right-hand. Keep the camera on the +z side, looking back toward −z, so
    // screen-right is world +x and screen-down is world +z.
    const tilt = THREE.MathUtils.degToRad(CAMERA.TILT_DEG);
    this.offset = new THREE.Vector3(
      0,
      Math.sin(tilt) * CAMERA.DISTANCE,
      Math.cos(tilt) * CAMERA.DISTANCE,
    );

    this.camera.position.copy(this.focus).add(this.offset);
    this.camera.lookAt(this.focus);
  }

  /** Snap straight to the target with no smoothing (used on (re)start). */
  snapTo(target: Vec2): void {
    this.focus.set(target.x, 0, target.y);
    this.camera.position.copy(this.focus).add(this.offset);
    this.camera.lookAt(this.focus);
  }

  /**
   * Follow `target`, optionally leading slightly in the direction of travel so the
   * player can see a bit more of where they're heading.
   */
  update(target: Vec2, velocity: Vec2, dt: number): void {
    const speed = Math.hypot(velocity.x, velocity.y);
    const leadX = speed > 1 ? (velocity.x / speed) * CAMERA.LOOK_AHEAD : 0;
    const leadZ = speed > 1 ? (velocity.y / speed) * CAMERA.LOOK_AHEAD : 0;

    // Frame-rate independent smoothing toward the (lead-adjusted) target.
    const k = smoothing(CAMERA.FOLLOW_LERP, dt);
    this.focus.x += (target.x + leadX - this.focus.x) * k;
    this.focus.z += (target.y + leadZ - this.focus.z) * k;

    this.camera.position.copy(this.focus).add(this.offset);
    this.camera.lookAt(this.focus);
  }
}
